// ShipMateHome.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTruck,
  faGlobe,
  faUsers,
  faDollarSign,
  faBoxOpen,
  faFileInvoiceDollar,
} from "@fortawesome/free-solid-svg-icons";

const ShipMateHome = () => {
  const [selectedSection, setSelectedSection] = useState("customer");

  // Dummy data
  const bloData = [
    { title: "First-Mile Pickup", count: 12 },
    { title: "Mid-Mile Transport", count: 8 },
    { title: "Last-Mile Delivery", count: 15 },
    { title: "Analytics Dashboard", count: "Active" },
    { title: "Parcel Pricing", count: "₹250–₹1200" },
  ];

  const gigJobs = [
    { role: "Driver", location: "Mumbai" },
    { role: "Packer", location: "Delhi" },
    { role: "Warehouse Staff", location: "Bangalore" },
    { role: "Loader", location: "Chennai" },
    { role: "Delivery Assistant", location: "Kolkata" },
  ];

  const importExportServices = [
    "Air Freight",
    "Sea Freight",
    "Surface Transport",
    "Customs Clearance",
    "Insurance",
    "Tracking & Documentation",
    "Freight Rate Calculator",
    "BLO Admin Oversight",
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 md:p-10">
      {/* Header */}
      <header className="text-center mb-8">
        <h1 className="text-xl md:text-2xl font-bold text-yellow-200">
          ShipMate – Boutique Logistics Service Provider Platfrom
        </h1>
        <p className="text-gray-300 mt-2">
          End-to-End solution: Customers, BLOs, Gig Workforce & International Shipping. Powered by Think5Whys
        </p>
      </header>

      {/* Navigation Tabs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-4 text-gray-100">
        {[
          { id: "customer", label: "Customer Booking" },
          { id: "blo", label: "Admin" },
          { id: "gig", label: "Workforce" },
          { id: "import", label: "Import/Export" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setSelectedSection(tab.id)}
            className={`px-4 py-2 rounded-full font-semibold transition ${
              selectedSection === tab.id
                ? "bg-indigo-600 text-white"
                : "bg-gray-800 border border-gray-700 text-gray-300 hover:bg-indigo-700 hover:text-white"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Sections */}
      <div className="space-y-10">
        {/* Customer Booking */}
        {selectedSection === "customer" && (
          <section className="bg-gray-800 p-6 rounded-xl shadow-md">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-yellow-200">
              <FontAwesomeIcon icon={faTruck} /> Customer Booking
            </h2>
            <form className="space-y-4">
              <input
                type="text"
                placeholder="Pickup Location"
                className="w-full p-3 rounded-md bg-gray-700 text-white border border-gray-600 mr-6"
              />
              <input
                type="text"
                placeholder="Delivery Location"
                className="w-full p-3 rounded-md bg-gray-700 text-white border border-gray-600"
              />
              <select className="h-12 w-full p-3 rounded-md bg-gray-700 text-white border border-gray-600">
                <option>Parcel Type</option>
                <option>Document</option>
                <option>Parcel</option>
                <option>Fragile</option>
              </select>
              <input
                type="number"
                placeholder="Weight (kg)"
                className="w-full p-3 rounded-md bg-gray-700 text-white border border-gray-600"
              />
              <select className="h-12 w-full p-3 rounded-md bg-gray-700 text-white border border-gray-600">
                <option>Delivery Speed</option>
                <option>Standard</option>
                <option>Express</option>
                <option>Same Day</option>
              </select>
              <button className="w-36 bg-indigo-600 text-white p-3 rounded-md font-semibold hover:bg-indigo-700">
                Book Parcel
              </button>
            </form>
          </section>
        )}

        {/* BLO Admin */}
        {selectedSection === "blo" && (
          <section className="bg-gray-800 p-6 rounded-xl shadow-md">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-yellow-200">
              <FontAwesomeIcon icon={faUsers} />Admin Overview
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {bloData.map((item, idx) => (
                <div
                  key={idx}
                  className="p-4 border rounded-md bg-indigo-900 text-indigo-200 font-semibold text-center"
                >
                  <p className="text-m">{item.title}</p>
                  <p className="mt-2 text-sm">{item.count}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Gig Workforce */}
        {selectedSection === "gig" && (
          <section className="bg-gray-800 p-6 rounded-xl shadow-md">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-indigo-300">
              <FontAwesomeIcon icon={faDollarSign} /> Gig / Blue-Collar Workforce
            </h2>
            <div className="space-y-4">
              {gigJobs.map((job, idx) => (
                <div
                  key={idx}
                  className="p-4 border rounded-md flex justify-between items-center bg-gray-500 text-green-200"
                >
                  <p>
                    {job.role} – {job.location}
                  </p>
                  <button className="w-16 bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700">
                    Assign
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Import/Export */}
        {selectedSection === "import" && (
          <section className="bg-gray-800 p-6 rounded-xl shadow-md">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-indigo-300">
              <FontAwesomeIcon icon={faGlobe} /> Import / Export
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {importExportServices.map((item, idx) => (
                <div
                  key={idx}
                  className="p-4 border rounded-md text-sm bg-blue-700 text-yellow-200 font-semibold text-center"
                >
                  {item}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ShipMateHome;
